import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  useRef,
} from 'react';
import React from 'react';
import { Form, Input, Table } from '@bedrock/components';
import type {
  FormInstance,
  FormItemProps,
  FormProps,
} from '@bedrock/components/lib/Form';
import type { ColumnType, TableProps } from '@bedrock/components/lib/Table';
import { useControllableValue, useMemoizedFn, useDebounceFn } from 'ahooks';

import { isEqual } from 'lodash';
import classname from 'classnames/bind';
import SFormItem from '../SForm/SFormItem';
import type { FormItemKeyType } from '../SForm/SFormItem';
import type { SFormComponentClass } from '../SForm/SFormElement';
import { formItemKeyToList } from '../SForm/SFormItem/utils';
import { useSForm } from '../SForm/useSForm';
import styles from './index.less';
const cx = classname.bind(styles);

// 为FormItem定义一个简化的类型，用于SFormTable内部使用
export type SimpleFormItemProps = {
  componentClass?: string;
  view?: boolean;
  options?: any[];
  elementProps?: any;
  className?: string;
  [key: string]: any;
};

// 创建SFormItem的工具函数
const createSafeFormItem = (
  config: SimpleFormItemProps,
  formItemKey: FormItemKeyType,
  title: string | React.ReactNode,
  required?: boolean
) => {
  // 转换componentClass为SFormComponentClass类型
  const componentClass = (config.componentClass ||
    'input') as SFormComponentClass;

  return (
    <SFormItem
      label={title as string}
      showLabel={false}
      required={required}
      {...config}
      className={cx({ 'view-item': !!config.view }, config?.className || '')}
      formItemKey={formItemKey}
      componentClass={componentClass}
    />
  );
};

// 定义表单列的类型
export interface FormColumn<T = any> extends Omit<ColumnType, 'dataIndex'> {
  dataIndex?: string | string[];
  formItem?:
    | SimpleFormItemProps
    | ((text: any, record: T, index: number) => SimpleFormItemProps);
  required?: boolean;
  title: string | React.ReactNode;
  show?: boolean;
  children?: FormColumn<T>[];
  listAddCell?: (
    record: T,
    index: number,
    opts: ColumnRenderParams<T>
  ) => React.ReactNode; // 如果有这个属性，则listChildren的最后会多一行，合并列单元格，listChildren有几列就合并列，默认显示添加列按钮
  listChildren?: FormColumn<T>[]; // 子列表children，如果是子列表，则表格这里数据是个数组，且子数据模型一致，通过分多行数据实现，关联父级数据的Column需要合并单元格
}

// 定义列渲染参数的类型
interface ColumnRenderParams<T = any> {
  add: (item?: T | T[], formIndex?: number) => void;
  remove: (index: number | number[]) => void;
}

type RecordType = Record<string, any>;

// 定义FormTable组件的属性类型
export interface FormTableProps<T extends RecordType = any>
  extends Omit<TableProps, 'data' | 'columns' | 'summary'> {
  value?: T[];
  onChange?: (value: T[]) => void;
  formColumns:
    | FormColumn<T>[]
    | ((p: ColumnRenderParams<T>) => FormColumn<T>[]);
  formProps?: Omit<FormProps, 'form' | 'initialValues'>;
  summary?: (data: T[], opts: ColumnRenderParams<T>) => React.ReactNode;
}

// 定义FormTable组件的ref类型
export interface FormTableRef<T = any> {
  add: (item?: T | T[], formIndex?: number) => void;
  remove: (index: number | number[]) => void;
  form: FormInstance<Record<'list', T[]>>;
  validateFields: () => Promise<boolean>;
}

// 定义FormTable组件
const SFormTable = forwardRef(function <T extends RecordType = any>(
  props: FormTableProps<T>,
  ref: React.Ref<FormTableRef<T>>
) {
  // 使用useControllableValue自定义hook管理表单的值
  const [list, setList] = useControllableValue<T[]>(props, {
    defaultValue: [] as T[],
  });

  // 本地list，用于区分内部和外部变化
  const localList = useRef<T[]>(list);
  // 使用Form.useForm创建表单实例
  const { form, formData, updateFormData } = useSForm<{ list: T[] }>({
    initialValues: { list },
  });
  // 从props中解构出formProps，并从formProps中解构出onValuesChange和restFormProps
  const {
    formProps = {},
    value,
    onChange,
    formColumns,
    summary,
    ...otherTableProps
  } = props;
  const { onValuesChange, ...restFormProps } = formProps;

  const asyncData = useMemoizedFn(() => {
    if (!isEqual(localList.current, list)) {
      form.setFieldsValue({ list: [...list] });
      localList.current = list;
    } else if (!isEqual(localList.current, formData?.list)) {
      localList.current = formData?.list;
      setList(formData?.list);
    }
  });

  // 使用useEffect,同步list和formData，区分内部和外部变化
  useEffect(() => {
    asyncData();
  }, [list, formData, asyncData]);

  // 定义添加表单项的方法
  const add = useMemoizedFn((item?: T | T[], formIndex?: number) => {
    const newList = Array.isArray(list) ? [...list] : [];

    if (item) {
      if (Array.isArray(item)) {
        if (
          typeof formIndex === 'number' &&
          formIndex >= 0 &&
          formIndex <= newList.length
        ) {
          newList.splice(formIndex, 0, ...item);
        } else {
          newList.push(...item);
        }
      } else {
        if (
          typeof formIndex === 'number' &&
          formIndex >= 0 &&
          formIndex <= newList.length
        ) {
          newList.splice(formIndex, 0, item);
        } else {
          newList.push(item);
        }
      }
    } else {
      if (
        typeof formIndex === 'number' &&
        formIndex >= 0 &&
        formIndex <= newList.length
      ) {
        newList.splice(formIndex, 0, {} as T);
      } else {
        newList.push({} as T);
      }
    }

    setList(newList);
  });

  // 定义删除表单项的方法
  const remove = useMemoizedFn((index: number | number[]) => {
    if (!Array.isArray(list) || list.length === 0) return;

    let newList: T[] = [];
    if (Array.isArray(index)) {
      newList = list.filter((_, i) => !index.includes(i));
    } else {
      newList = list.filter((_, i) => i !== index);
    }
    setList(newList);
  });

  const hasValidateFields = useRef(false);

  const validateFields = useMemoizedFn(() => {
    hasValidateFields.current = true;
    return new Promise<boolean>((resolve, reject) => {
      if (form.validateFields) {
        form
          .validateFields()
          .then((values) => {
            // 验证成功，返回true
            resolve(true);
          })
          .catch((error) => {
            // 验证失败，返回错误信息
            reject(error?.errorFields?.[0]?.errors?.[0] || undefined);
          });
      } else {
        resolve(true);
      }
    });
  });

  const { run: validateFieldsDebounce } = useDebounceFn(form.validateFields, {
    wait: 1000,
  });

  const onTableScroll = useMemoizedFn((...args: any[]) => {
    if (hasValidateFields.current) {
      validateFieldsDebounce();
    }
    props.onScroll?.(args[0]);
  });

  // 使用useImperativeHandle自定义ref的暴露值
  useImperativeHandle(
    ref,
    () => ({
      add,
      remove,
      form,
      validateFields,
    }),
    [add, remove, form, validateFields]
  );

  const renderTitle = (
    title: string | React.ReactNode,
    required?: boolean,
    index?: number
  ): React.ReactNode => {
    return (
      <span key={index}>
        {required && <span className={cx('required-start')}>*</span>}
        {title}
      </span>
    );
  };

  // 使用useMemo计算表单列
  const columns = useMemo(() => {
    let columnsRes = formColumns;
    if (typeof columnsRes === 'function') {
      columnsRes = columnsRes({ add, remove });
    }

    return (
      columnsRes
        ?.map?.((column: FormColumn<T>, index: number) => {
          const { formItem, required, ...otherColumn } = column;

          if (column?.children?.length) {
            return {
              ...otherColumn,
              title: renderTitle(column.title, column.required, index),
              className: cx('table-form-column', column.className),
              children: column.children.map(
                (childColumn: FormColumn<T>, childIndex: number) => {
                  const { formItem: childFormItem } = childColumn;

                  return {
                    key: childIndex,
                    ...childColumn,
                    title: renderTitle(
                      childColumn.title,
                      childColumn.required,
                      childIndex
                    ),
                    className: cx(
                      'table-form-childColumn',
                      childColumn.className
                    ),
                    render: (text: any, record: T, rowIndex: number) => {
                      if (!childFormItem) {
                        return childColumn.render
                          ? childColumn.render(text, record, rowIndex)
                          : '';
                      }

                      // 处理formItem配置
                      const formItemConfig: SimpleFormItemProps =
                        typeof formItem === 'function'
                          ? formItem(text, record, rowIndex)
                          : formItem || {};

                      // 处理子列formItem配置
                      const childFormItemConfig: SimpleFormItemProps =
                        typeof childFormItem === 'function'
                          ? childFormItem(text, record, rowIndex)
                          : childFormItem || {};

                      // 处理dataIndex
                      const columnDataIndex = Array.isArray(column.dataIndex)
                        ? column.dataIndex.join('.')
                        : typeof column.dataIndex === 'string'
                        ? column.dataIndex
                        : '';

                      const childColumnDataIndex = Array.isArray(
                        childColumn.dataIndex
                      )
                        ? childColumn.dataIndex.join('.')
                        : typeof childColumn.dataIndex === 'string'
                        ? childColumn.dataIndex
                        : '';

                      // 构建formItemKey
                      const formItemKeyValue = [
                        rowIndex,
                        ...(formItemKeyToList(columnDataIndex)
                          ? [formItemKeyToList(columnDataIndex)].flat()
                          : []),
                        childIndex,
                        ...(formItemKeyToList(childColumnDataIndex) || []),
                      ].flat();

                      return createSafeFormItem(
                        childFormItemConfig,
                        formItemKeyValue,
                        childColumn.title,
                        childColumn.required
                      );
                    },
                  };
                }
              ),
            };
          }

          return {
            key: index,
            ...otherColumn,
            title: renderTitle(column.title, column.required, index),
            className: cx('table-form-column', column.className),
            render: (text: any, record: T, rowIndex: number) => {
              if (!formItem) {
                return column.render
                  ? column.render(text, record, rowIndex)
                  : text;
              }

              // 处理formItem配置
              const formItemConfig: SimpleFormItemProps =
                typeof formItem === 'function'
                  ? formItem(text, record, rowIndex)
                  : formItem;

              // 处理dataIndex
              const columnDataIndex = Array.isArray(column.dataIndex)
                ? column.dataIndex.join('.')
                : typeof column.dataIndex === 'string'
                ? column.dataIndex
                : '';

              // 构建formItemKey
              const formItemKeyValue = [
                rowIndex,
                ...(formItemKeyToList(columnDataIndex)
                  ? [formItemKeyToList(columnDataIndex)].flat()
                  : []),
              ].flat();

              return createSafeFormItem(
                formItemConfig,
                formItemKeyValue,
                column.title,
                column.required
              );
            },
          };
        })
        .filter((n) => n.show !== false) || []
    );
  }, [
    formColumns,
    add,
    remove,
    renderTitle,
    formItemKeyToList,
    createSafeFormItem,
  ]);

  // 返回FormTable组件的JSX
  return (
    <Form
      {...restFormProps}
      form={form}
      initialValues={{ list: list || [] }}
      onValuesChange={(changedValues, allValues) => {
        updateFormData();
        if (onValuesChange) {
          onValuesChange(changedValues, allValues);
        }
      }}
    >
      <Form.List name="list">
        {(fields) => {
          return (
            <Table
              // @ts-ignore
              rowKey={(_, index: number) => `${index}`}
              {...otherTableProps}
              className={cx('table-form', otherTableProps.className)}
              columns={columns as any}
              data={(list as any) || []}
              onScroll={onTableScroll}
              summary={
                summary
                  ? (data: any) => summary?.(data, { add, remove })
                  : undefined
              }
            />
          );
        }}
      </Form.List>
    </Form>
  );
});

export default SFormTable;
